// @ts-nocheck
/* =============================================================================
 * Sequoia — Dynamic Social Proof
 * Vanilla ES6. No dependencies, no jQuery, no app.
 * ---------------------------------------------------------------------------
 * Two independent features, both fed by the JSON blobs printed by
 * sections/social-proof.liquid:
 *   • Bestseller popup   — rotates random eligible products + random messages,
 *                          session-capped, gently paced, fades in/out.
 *   • Live viewers pill  — realistic per-product "N watching now" counters that
 *                          drift slowly and independently, lazy-initialised on
 *                          scroll via IntersectionObserver.
 * ========================================================================== */

(function () {
  "use strict";

  /* ---------------------------------------------------------------------------
   * 0. Small utilities
   * ------------------------------------------------------------------------ */

  /** Random integer in [min, max] inclusive. */
  function randInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /** Read + parse a JSON <script> blob by id. Returns null on any failure. */
  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /** Stable 32-bit-ish hash of a string — used to seed per-product values so a
   *  product always shows a consistent baseline (Iron ~2, Probiotics ~5, ...). */
  function hashString(str) {
    var h = 0;
    str = String(str);
    for (var i = 0; i < str.length; i++) {
      h = (h << 5) - h + str.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  }

  var prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------------------------------------------------------------------------
   * 1. Config
   * ------------------------------------------------------------------------ */
  var config = readJSON("sp-config") || {};
  var popupCfg = config.popup || {};
  var viewersCfg = config.viewers || {};

  /* Preview/QA mode: add ?sp_preview=1 to any storefront URL to bypass the
   * per-session cap and show the popup almost immediately (rotating fast).
   * Real visitors are unaffected. */
  var PREVIEW = /[?&]sp_preview=1/.test(window.location.search);

  /* ===========================================================================
   * 2. BESTSELLER POPUP
   * ======================================================================== */
  var Popup = (function () {
    var SESSION_KEY = "sp_popup_count";
    var el, linkEl, imgEl, msgEl, titleEl, closeEl;
    var products = [];
    var messages = [];
    var lastProductId = null;
    var lastMessage = null;
    var hideTimer = null;
    var nextTimer = null;

    function shownCount() {
      return parseInt(sessionStorage.getItem(SESSION_KEY) || "0", 10) || 0;
    }
    function bumpCount() {
      if (PREVIEW) return; // don't accumulate while previewing
      try {
        sessionStorage.setItem(SESSION_KEY, String(shownCount() + 1));
      } catch (e) {
        /* private mode — degrade silently */
      }
    }

    /** Pick a random item, avoiding an immediate repeat of `last`. */
    function pickDifferent(list, last, keyFn) {
      if (list.length === 0) return null;
      if (list.length === 1) return list[0];
      var choice;
      var guard = 0;
      do {
        choice = list[randInt(0, list.length - 1)];
        guard++;
      } while (keyFn(choice) === last && guard < 12);
      return choice;
    }

    function show() {
      var product = pickDifferent(products, lastProductId, function (p) {
        return p.id;
      });
      var message = pickDifferent(messages, lastMessage, function (m) {
        return m;
      });
      if (!product || !message) return;

      lastProductId = product.id;
      lastMessage = message;

      msgEl.textContent = message;
      titleEl.textContent = product.title || "";
      linkEl.setAttribute("href", product.url || "#");
      if (product.image) {
        imgEl.setAttribute("src", product.image);
        imgEl.setAttribute("alt", product.title || "");
        imgEl.style.display = "";
      } else {
        imgEl.style.display = "none";
      }

      el.hidden = false;
      el.setAttribute("aria-hidden", "false");
      // Force a reflow so the transition runs from the hidden state.
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight;
      el.classList.add("is-visible");

      bumpCount();

      var visibleMs = (popupCfg.visible || 5) * 1000;
      hideTimer = window.setTimeout(hide, visibleMs);
    }

    function hide() {
      window.clearTimeout(hideTimer);
      el.classList.remove("is-visible");
      el.setAttribute("aria-hidden", "true");

      var animMs = prefersReducedMotion ? 1 : popupCfg.animMs || 500;
      window.setTimeout(function () {
        el.hidden = true;
      }, animMs);

      scheduleNext(false);
    }

    /** Schedule the next popup. `first` uses the initial delay range, otherwise
     *  the (longer) cooldown range. Stops once the session cap is reached. */
    function scheduleNext(first) {
      window.clearTimeout(nextTimer);
      if (!PREVIEW && shownCount() >= (popupCfg.maxPerSession || 3)) return;

      var delayMs;
      if (PREVIEW) {
        // Fast, uncapped cadence for QA.
        delayMs = first ? 1200 : 3000;
      } else {
        var min = first ? popupCfg.delayMin : popupCfg.cooldownMin;
        var max = first ? popupCfg.delayMax : popupCfg.cooldownMax;
        delayMs = randInt(min || 15, max || 40) * 1000;
      }

      nextTimer = window.setTimeout(function () {
        // Skip while the tab is hidden; re-check shortly after.
        if (document.hidden) {
          scheduleNext(false);
          return;
        }
        show();
      }, delayMs);
    }

    function init() {
      if (!popupCfg.enabled) return;
      if (!PREVIEW && shownCount() >= (popupCfg.maxPerSession || 3)) return;

      el = document.getElementById("sp-popup");
      if (!el) return;
      linkEl = el.querySelector(".sp-popup__link");
      imgEl = el.querySelector(".sp-popup__img");
      msgEl = el.querySelector(".sp-popup__msg");
      titleEl = el.querySelector(".sp-popup__title");
      closeEl = el.querySelector(".sp-popup__close");

      products = readJSON("sp-bestsellers") || [];
      if (products.length === 0) return; // nothing eligible → never show

      messages = String(popupCfg.messages || "")
        .split("\n")
        .map(function (m) {
          return m.trim();
        })
        .filter(Boolean);
      if (messages.length === 0) return;

      // Apply configured animation duration to the element.
      if (popupCfg.animMs) {
        el.style.setProperty("--sp-anim", popupCfg.animMs + "ms");
      }

      closeEl.addEventListener("click", function () {
        hide();
      });

      scheduleNext(true);
    }

    return { init: init };
  })();

  /* ===========================================================================
   * 3. LIVE VIEWERS PILL
   * ======================================================================== */
  var Viewers = (function () {
    var min, max, updateMin, updateMax;

    /** Resolve a stable identity for a pill from the nearest product wrapper. */
    function productKey(pill, index) {
      // 1) explicit id printed on the pill (collection/grid cards)
      if (pill.dataset.spProduct) return pill.dataset.spProduct;
      // 2) nearest product wrapper (product page / new block cards)
      var host = pill.closest("[data-product-id]");
      if (host && host.getAttribute("data-product-id")) {
        return host.getAttribute("data-product-id");
      }
      // 3) last resort — positional
      return "idx-" + index;
    }

    /** Baseline count for a product — stable across reloads, distinct per id. */
    function baseFor(key) {
      var span = max - min + 1;
      return min + (hashString(key) % span);
    }

    function render(pill, value) {
      var countEl = pill.querySelector(".sp-viewers__count");
      if (!countEl || countEl.textContent === String(value)) return;
      countEl.textContent = value;
      // Tiny scale bump so a change reads as "live" without being flashy.
      if (!prefersReducedMotion) {
        countEl.classList.add("sp-bump");
        window.setTimeout(function () {
          countEl.classList.remove("sp-bump");
        }, 300);
      }
    }

    /** Gentle random walk (±1, occasionally steady) clamped to [min, max]. */
    function nextValue(current) {
      var roll = Math.random();
      var delta;
      if (current <= min) {
        delta = roll < 0.75 ? 1 : 0;
      } else if (current >= max) {
        delta = roll < 0.75 ? -1 : 0;
      } else {
        delta = roll < 0.4 ? -1 : roll < 0.8 ? 1 : 0;
      }
      return current + delta;
    }

    function activate(pill, index) {
      if (pill.dataset.spInit) return;
      pill.dataset.spInit = "1";

      var key = productKey(pill, index);
      var value = baseFor(key);

      // Fill static parts.
      var iconEl = pill.querySelector(".sp-viewers__icon");
      var labelEl = pill.querySelector(".sp-viewers__label");
      if (iconEl) iconEl.textContent = viewersCfg.icon || "👁";

      var setLabel = function (v) {
        if (!labelEl) return;
        labelEl.textContent =
          v === 1
            ? viewersCfg.textSingular || ""
            : viewersCfg.textPlural || "";
      };

      render(pill, value);
      setLabel(value);
      pill.hidden = false;
      pill.setAttribute("aria-hidden", "false");
      // Reveal on next frame for the fade.
      window.requestAnimationFrame(function () {
        pill.classList.add("is-active");
      });

      /** Self-rescheduling tick with a fresh random interval every time, so no
       *  two products update together and the cadence never looks mechanical. */
      function tick() {
        window.setTimeout(function () {
          value = nextValue(value);
          render(pill, value);
          setLabel(value);
          tick();
        }, randInt(updateMin, updateMax) * 1000);
      }
      // Stagger the very first update so cards started together still diverge.
      window.setTimeout(tick, randInt(0, updateMin) * 1000);
    }

    function init() {
      if (!viewersCfg.enabled) return;

      min = viewersCfg.min || 1;
      max = viewersCfg.max || 7;
      if (max < min) max = min;
      updateMin = viewersCfg.updateMin || 30;
      updateMax = viewersCfg.updateMax || 90;
      if (updateMax < updateMin) updateMax = updateMin;

      var pills = document.querySelectorAll(".sp-viewers:not([data-sp-init])");
      if (pills.length === 0) return;

      // Lazy-init: only when the pill actually scrolls into view.
      if ("IntersectionObserver" in window) {
        var io = new IntersectionObserver(
          function (entries, obs) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              var pill = entry.target;
              obs.unobserve(pill);
              activate(pill, Array.prototype.indexOf.call(pills, pill));
            });
          },
          { rootMargin: "80px" }
        );
        pills.forEach(function (pill) {
          io.observe(pill);
        });
      } else {
        // Fallback: activate all immediately.
        pills.forEach(function (pill, i) {
          activate(pill, i);
        });
      }
    }

    return { init: init };
  })();

  /* ---------------------------------------------------------------------------
   * 4. Boot
   * ------------------------------------------------------------------------ */
  function boot() {
    Popup.init();
    Viewers.init();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
